import { htmlSafe } from '@ember/template';
import {
  getAvatarInnerSize,
  getAvatarMonogramFontSize,
  getAvatarMonogramVerticalOffset,
  getAvatarPhotoSize,
  getMarkerBorderWidth,
  getMarkerInnerSize,
} from 'garaje/utils/map-marker-scale';

/**
 * Extract display initials from a full name or email address.
 *
 * Names are split on spaces; emails are split on the prefix delimiters
 * (`.`, `-`, `_`, `+`). A leading `+<digits>` suffix (e.g. "+3 more")
 * is stripped before processing.
 *
 * @param {string} input — full name or email
 * @returns {string} 1–4 uppercase characters
 */
export function monogramInitials(input) {
  let nameAttr = input ?? '';
  if (/\+\d+/.test(nameAttr)) {
    nameAttr = nameAttr.split('+')[0];
  }
  if (nameAttr.includes('@')) {
    const prefix = nameAttr.split('@')[0];
    const parts = prefix.split(/[.\-_+]/).filter(Boolean);
    return parts
      .map((p) => p.charAt(0))
      .join('')
      .slice(0, 2);
  }
  const nameArray = nameAttr.split(' ');
  let name = '';
  for (let i = 0; i < nameArray.length; i++) {
    name += nameArray[i].slice(0, 1);
  }
  return name.slice(0, 4);
}

/**
 * Build the outer circular marker shell used by both the live map and
 * the move-request map. Mirrors `resource-map`'s
 * `getCircularMarkerShellHtml` without the per-instance icon cache.
 *
 * @param {object}  options
 * @param {number}  [options.markerSize=30]
 * @param {string}  [options.backgroundColor='']
 * @param {string}  [options.innerHtml='']
 * @param {string}  [options.testSelector='']
 * @param {number|null} [options.contentAreaSize=null]
 * @returns {import('@ember/template').SafeString}
 */
export function buildCircularMarkerShellHtml({
  markerSize = 30,
  backgroundColor = '',
  innerHtml = '',
  testSelector = '',
  contentAreaSize = null,
} = {}) {
  const borderWidth = getMarkerBorderWidth(markerSize);
  const resolvedContentAreaSize = contentAreaSize ?? getMarkerInnerSize(markerSize);
  return htmlSafe(
    `<div aria-hidden="true" class="relative flex items-center justify-center rounded-full overflow-hidden shrink-0"` +
      ` style="width:${markerSize}px;height:${markerSize}px;box-sizing:border-box;border:${borderWidth}px solid white;${backgroundColor ? `background-color:${backgroundColor};` : ''}box-shadow:0 5.4px 10.7px 0 rgba(0,0,0,0.12);"` +
      ` ${testSelector}>` +
      `<div class="flex items-center justify-center shrink-0" style="width:${resolvedContentAreaSize}px;height:${resolvedContentAreaSize}px;position:relative;z-index:1;">` +
      `${innerHtml}` +
      `</div></div>`,
  );
}

/**
 * Render a profile-picture marker icon.
 *
 * @param {string} src — image URL
 * @param {number} [markerSize=30]
 * @returns {import('@ember/template').SafeString}
 */
export function buildPhotoMarkerHtml(src, markerSize = 30) {
  const photoSize = getAvatarPhotoSize(markerSize);
  return buildCircularMarkerShellHtml({
    markerSize,
    testSelector: 'data-test-profile-picture',
    contentAreaSize: photoSize,
    innerHtml:
      `<img alt="" class="block object-cover object-center rounded-full"` +
      ` style="width:${photoSize}px;height:${photoSize}px;"` +
      ` src="${src}" role="presentation" data-test-entry-thumbnail>`,
  });
}

/**
 * Render an initials-monogram marker icon.
 *
 * @param {string} displayName — full name or email to derive initials from
 * @param {number} [markerSize=30]
 * @returns {import('@ember/template').SafeString}
 */
export function buildInitialsMarkerHtml(displayName, markerSize = 30) {
  const initials = monogramInitials(displayName);
  const innerSize = getAvatarInnerSize(markerSize);
  const fontSize = getAvatarMonogramFontSize(markerSize, initials.length);
  const verticalOffset = getAvatarMonogramVerticalOffset(markerSize);
  const letterSpacing = initials.length > 1 ? `${(-fontSize * 0.04).toFixed(2)}px` : '0';
  return buildCircularMarkerShellHtml({
    markerSize,
    backgroundColor: 'hsl(224, 8%, 48%)',
    testSelector: 'data-test-monogram',
    innerHtml:
      `<div data-test-entry-monogram` +
      ` style="display:flex;align-items:center;justify-content:center;width:${innerSize}px;height:${innerSize}px;font-size:${fontSize}px;font-weight:600;letter-spacing:${letterSpacing};line-height:1;color:#FFFFFF;text-transform:uppercase;margin:0;transform:translateY(${verticalOffset}px);">` +
      `${initials}</div>`,
  });
}
